import { useEffect, useRef, useState } from 'react';
import { Columns3 } from 'lucide-react';
import type { ColVisibility } from '../hooks/useColVisibility';
import './ColumnSettings.css';
import { Toggle } from './Toggle';

interface ColumnSettingsProps {
  vis: ColVisibility;
  /** Announced to screen readers; also the tooltip. */
  label?: string;
}

/**
 * Show/hide columns for one data table. Sits top-right of the table, beside
 * whatever hint text the page already has there.
 *
 * The count badge is the point: a hidden column is easy to forget about, and
 * "why is Who missing" is a support question we'd rather answer on the page.
 */
export function ColumnSettings({
  vis,
  label = 'Show or hide columns',
}: ColumnSettingsProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // Close on an outside click or Escape. Bound only while open so the page
  // isn't carrying two listeners per table for a menu nobody opened.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggleable = vis.columns.filter((c) => !c.locked);

  return (
    <div className="colset" ref={rootRef}>
      <button
        type="button"
        className={`colset-btn${open ? ' colset-btn-open' : ''}`}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="true"
        aria-label={label}
        title={label}
      >
        <Columns3 size={16} strokeWidth={1.75} aria-hidden />
        {vis.hiddenCount > 0 && (
          <span className="colset-badge">{vis.hiddenCount}</span>
        )}
      </button>

      {open && (
        <div className="colset-menu" role="group" aria-label={label}>
          <p className="colset-menu-title">Columns</p>
          {toggleable.map((c) => (
            <Toggle
              key={c.key}
              checked={vis.isVisible(c.key)}
              onChange={() => vis.toggle(c.key)}
              label={c.label}
              size="sm"
              className="colset-row"
            />
          ))}
          <button type="button" className="colset-reset" onClick={vis.reset}>
            Reset to default
          </button>
        </div>
      )}
    </div>
  );
}
